

import { useEffect, useRef, useState } from "react";
import { Box, useToast } from "@chakra-ui/react";
import BreadCrumbMaker from "../../../components/BreadCrumbMaker";
import Form from "./Form";
import { useNavigate, useParams } from "react-router-dom";
import {
  getFleetDetails,
  updateFleetDetails,
} from "../../../actions/inventory/fleet.action";
import fleetFormSchema from "./Validation.jsx";

const EditFleet = () => {
  const { id } = useParams();
  const toast = useToast();
  const navigate = useNavigate();
  const formRef = useRef(null);
  const [files, setFiles] = useState({});
  const [initialValues, setInitialValues] = useState(null);

  useEffect(() => {
    const loadFleet = async () => {
      try {
        const res = await getFleetDetails(id);
        const fleet = res.data;


        const formatDate = (dateStr) => {
          if (!dateStr) return "";
          const d = new Date(dateStr);
          return d.toISOString().split("T")[0];
        };

        setInitialValues({
          ...fleet,
          ownerDetails: fleet.ownerDetails,
          regEndDate: formatDate(fleet.regEndDate),
          insuranceExpiryDate: formatDate(fleet.insuranceExpiryDate),
          fitnessEndDate: formatDate(fleet.fitnessEndDate),
          pucEndDate: formatDate(fleet.pucEndDate),
          permitEndDate: formatDate(fleet.permitEndDate),
          taxPaidTillDate: formatDate(fleet.taxPaidTillDate),
          operationalStatus: fleet.operationalStatus?.toString() || "",
          administrativeStatus: fleet.administrativeStatus?.toString() || "",
        });
      } catch (err) {
        console.error(err);
        toast({
          title: "Error",
          description: "Error while fetching fleet details",
          status: "error",
          duration: 5000,
          isClosable: true,
        });
      }
    };

    loadFleet();
  }, [id]);

  const handleSubmit = async (values) => {
    try {
      // console.log("Edit Submit Values:", values);
      // console.log("Edit Submit Data:", data);

      const merged = { ...values, ...files };
      delete merged.ownerDetails;

      const formData = new FormData();
      const fileFields = new Set([
        "registrationFront",
        "registrationBack",
        "insuranceImage",
        "fitnessCertificate",
        "pucCertificate",
        "permitCertificate",
        "taxDocument",
        "vehicleImage",
        "ownerPhoto",
        "ownershipAgreement",
      ]);

      Object.entries(merged).forEach(([key, value]) => {
        if (value === undefined || value === null || value === "") return;

        // Skip any uploaded file not allowed by update endpoint to avoid multer "Unexpected field".
        if (value instanceof File && !fileFields.has(key)) return;
        if (
          Array.isArray(value) &&
          value.length > 0 &&
          value[0] instanceof File &&
          !fileFields.has(key)
        ) {
          return;
        }

        if (fileFields.has(key)) {
          if (value instanceof File) {
            formData.append(key, value);
          } else if (Array.isArray(value) && value.length > 0 && value[0] instanceof File) {
            formData.append(key, value[0]);
          }
          return;
        }

        if (["year", "seatingCapacity"].includes(key)) {
          const num = Number(value);
          if (!Number.isNaN(num)) formData.append(key, num);
          return;
        }

        // Keep status values as strings so "0" is not treated as falsy by backend update checks.
        if (["operationalStatus", "administrativeStatus"].includes(key)) {
          formData.append(key, String(value));
          return;
        }

        if (key.toLowerCase().includes("date")) {
          const d = new Date(value);
          if (!isNaN(d.getTime())) formData.append(key, d.toISOString());
          return;
        }

        formData.append(key, value);
      });

      // console.log("=== Edit FormData ===");
      // for (let [k, v] of formData.entries()) {
      //   console.log(k, v instanceof File ? v.name : v);
      // }

      const res = await updateFleetDetails(id, formData);

      if (res.success) {
        toast({
          title: "Success",
          description: "Fleet updated successfully",
          status: "success",
          duration: 4000,
          isClosable: true,
        });
        navigate("/fleet-management/vehicles");
      }
    } catch (err) {
      console.error(err);
      toast({
        title: "Error",
        description: err.message || "Something went wrong",
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    }
  };

  if (!initialValues) return null;

  return (
    <Box px={6} py={8}>
      <BreadCrumbMaker
        items={[
          { link: "", title: "Fleet Management" },
          { link: "/fleet-management/vehicles", title: "Vehicle" },
          { link: "", title: "Edit Vehicle", on: true },
        ]}
      />

      <Form
        initialValues={initialValues}
        files={files}
        setFiles={setFiles}
        handleSubmit={handleSubmit}
        formRef={formRef}
        validationSchema={fleetFormSchema}
      />
    </Box>
  );
};

export default EditFleet;